import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, ElementRef, EventEmitter, HostBinding, HostListener,
  Input, NgZone, OnChanges, OnInit,
  OnDestroy, Output,
  SimpleChanges, ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { PdfViewerComponent } from 'ng2-pdf-viewer';
import { PerfectScrollbarComponent } from 'ngx-perfect-scrollbar'; // needed to reach the real scrollable element for pan, see getPdfScrollElement()

import { PartnersTypeEnum } from '../../../partners/corporate/enums';
import { CustomerTypeEnum, GeneralPartnerTypeEnum } from 'common/src/models';
import { AppState } from '../../../store/state/app.state';
import { selectCompanyProfile } from '../../../administration/store/selectors';
import { CompanyProfile } from '../../../administration/models/company-profile.model';
import { FileService } from 'common/src/services/file.service';
import { DocumentTypesUppercaseEnum } from 'common/src/modules/modals/modals-common/link-document-modal/enums/ducument-types.enum';
import { ContextMenuItemAction, ContextMenuItemModel } from 'common/src/models/context-menu.model';
import { NavBarBtnModel } from 'common/src/models';
import { STATUS_LABEL_CLASS } from '../../constants/status-label-class';

const FOOTER_SPINNER_COLOR = '#5E6681';
const FOOTER_ACTIONS_WITHOUT_SPINNER: string[] = [ContextMenuItemAction.EDIT];

export interface DocumentPdfPreviewProgressItemModel {
  label: string;
  value: number;
  maxValue: number;
}

@Component({
  selector: 'rnpl-document-pdf-preview',
  templateUrl: './document-pdf-preview.component.html',
  styleUrls: ['./document-pdf-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentPdfPreviewComponent implements OnInit, OnDestroy, OnChanges {
  public expanded = false;
  public resourceUrl: string;
  public pdfUnavailable = false;
  public isPreviewLoading = false;
  public companyProfile: CompanyProfile;

  public previewScale = 1;
  public currentPage = 1;
  public totalPages = 0;
  private resizeObserver: any;
  private lastRenderedWidth: number = null;
  private resizeSettleTimeout: any;

  private pdfLoaded = false;
  private imageLoaded = false;

  public partnersTypeEnum = PartnersTypeEnum;
  public generalPartnerTypeEnum = GeneralPartnerTypeEnum;
  public contextMenuItemAction = ContextMenuItemAction;

  public trackByAction = (index: number, action: ContextMenuItemModel): string => action.action;

  public statusLblClasses: { [key: string]: string } = STATUS_LABEL_CLASS;

  @Input() public documentId: number;
  @Input() public logoUrl: string;
  @Input() public documentType: DocumentTypesUppercaseEnum;
  @Input() public title: string;
  @Input() public partnerType: PartnersTypeEnum|CustomerTypeEnum;
  @Input() public partnerName: string;
  @Input() public generalPartnerType: GeneralPartnerTypeEnum;
  @Input() public actions: ContextMenuItemModel[];
  @Input() public documentStatus: string;

  public get hasPartnerVisual(): boolean {
    if (this.logoUrl) { return true; }
    if (this.partnerType === this.partnersTypeEnum.GENERAL) { return !!this.generalPartnerType; }
    return !!this.partnerType && !!this.partnerName;
  }

  public get fileNotUploadedKey(): string {
    switch (this.documentType) {
      case DocumentTypesUppercaseEnum.PO:
        return 'PO.ORDER_FILE_NOT_UPLOAD';
      case DocumentTypesUppercaseEnum.CRN:
      case DocumentTypesUppercaseEnum.ICN:
        return 'CREDIT_NOTE.CREDIT_NOTE_FILE_NOT_UPLOAD';
      default:
        return 'IIN.INVOICE_FILE_NOT_UPLOAD';
    }
  }
  @Input() public allocatePaymentAvailable = false;
  @Input() public downloadFileAvailable = false;
  @Input() public footerActions: NavBarBtnModel[] = [];
  @Input() public clearLoadingAction$: Observable<string>;
  @Input() public customFooterAvailable = false;

  public loadingAction: string = null;
  public renderedFooterActions: NavBarBtnModel[] = [];
  private clearLoadingSubscription: Subscription;
  @Input() public sourceMode: 'generated' | 'file' | 'custom' = 'generated';

  @Input() public pdfDisabled = false;

  @Input() public fileUrl: string | null = null;
  @Input() public fileType: string | null = null;

  @Input() public showOpenButton = true;
  @Input() public openDocumentUrl: string = null;
  @Input() public fileLoading = false;
  @Input() public progressItems: DocumentPdfPreviewProgressItemModel[] = [];
  @Input() public progressLoading = false;
  @Input() public compact = false;

  // PDF/image zoom + click-and-drag pan. Off by default (`false`) for every page using this
  // shared component; a page opts in explicitly with [enableZoom]="true"
  @Input() public enableZoom = false;
  private readonly zoomMin = 1;
  private readonly zoomMax = 2;
  private readonly zoomStep = 0.25;
  public zoomLevel = this.zoomMin;
  public isPanning = false;
  private panStartX = 0;
  private panStartY = 0;
  private panScrollLeft = 0;
  private panScrollTop = 0;
  // read as PerfectScrollbarComponent, NOT ElementRef: the <perfect-scrollbar> host tag itself has
  // overflow:hidden and never scrolls — the real scrollable node is its internal directiveRef's
  // element (a nested .ps div), see onPdfScrollElement() below.
  @ViewChild('pdfScrollContainer', { static: false }) private pdfScrollContainerRef: PerfectScrollbarComponent;

  @Output() actionEmitted: EventEmitter<string> = new EventEmitter<string>();

  public fullyExpanded = false;
  @Output() fullyExpandedChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  private fullyExpandedTimeout: any = null;

  @HostBinding('class.collapsed') get isPreviewCollapsed() {
    return !this.expanded;
  }

  @HostBinding('class.expanded') get isPreviewExpanded() {
    return this.expanded;
  }

  @HostBinding('class.compact') get isCompactHost() {
    return this.compact;
  }

  @HostBinding('class.image-preview') get isImagePreviewHost() {
    return this.compact && this.isImagePreview;
  }

  @HostBinding('class.pdf-ready') get isPdfReadyHost() {
    if (this.isImagePreview) { return false; }
    return this.compact && !this.showLoader && !this.pdfUnavailable;
  }

  @HostBinding('style.height.px') public hostHeightPx: number | null = null;

  @ViewChild(PdfViewerComponent, { static: false }) private pdfViewerComponent: PdfViewerComponent;

  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private readonly fileService: FileService,
    private readonly store: Store<AppState>,
    private readonly cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
    private readonly ngZone: NgZone, // used to run pan drag-tracking outside Angular's zone, see onPdfPanStart()
    private readonly router: Router,
  ) {
    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => {
        this.companyProfile = companyProfile;
      });
  }

  ngOnInit(): void {
    if (!this.compact) {
      this.setupResizeObserver();
    }
  }

  public get showLoader(): boolean {
    if (this.sourceMode === 'custom') { return false; } // no fetch happens in this mode at all
    if (!this.documentId) { return false; }
    if (this.isPreviewLoading) { return true; }
    if (this.pdfUnavailable) { return false; }
    if (this.resourceUrl) { return !this.pdfLoaded; }
    if (this.sourceMode === 'file' && this.fileType !== 'pdf' && this.fileUrl) { return !this.imageLoaded; }
    return false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.hasOwnProperty('clearLoadingAction$')) {
      this.subscribeToClearLoading();
    }
    if (changes && changes.hasOwnProperty('documentId') && !changes.documentId.firstChange) {
      this.loadingAction = null;
    }
    if (changes && (changes.hasOwnProperty('footerActions') || changes.hasOwnProperty('documentId'))) {
      this.updateRenderedFooterActions();
    }

    if (this.sourceMode === 'custom') { return; }

    if (this.sourceMode === 'file') {
      const fileRelevantChange = !changes
        || changes.hasOwnProperty('fileUrl')
        || changes.hasOwnProperty('fileType')
        || changes.hasOwnProperty('fileLoading')
        || changes.hasOwnProperty('sourceMode')
        || changes.hasOwnProperty('documentId');
      if (!fileRelevantChange) { return; }

      this.resetZoom();
      this.pdfLoaded = false;
      this.imageLoaded = false;
      this.clearPendingHeightHandoff();
      this.resetImageHostHeight();
      this.hostHeightPx = null;
      this.isPreviewLoading = this.fileLoading;
      this.pdfUnavailable = !this.fileLoading && !this.fileUrl;
      this.resourceUrl = (this.fileType === 'pdf') ? this.fileUrl : null;
      return;
    }

    if (changes && (
      changes.hasOwnProperty('documentId')
      || changes.hasOwnProperty('sourceMode')
      || changes.hasOwnProperty('pdfDisabled')
    )) {
      this.resetZoom();
      this.currentPage = 1;
      this.totalPages = 0;
      this.clearPendingHeightHandoff();
      this.hostHeightPx = null;

      if (this.pdfDisabled) {
        this.resourceUrl = null;
        this.pdfUnavailable = true;
        this.isPreviewLoading = false;
        return;
      }

      if (this.documentType && this.documentId) {
        this.getPdfUrl();
      }
    }
  }

  public getPdfUrl(): void {
    this.pdfLoaded = false;
    this.pdfUnavailable = false;
    this.isPreviewLoading = true;

    this.resourceUrl = null;

    const requestedDocumentId = this.documentId;

    this.fileService.previewPdfFile(
      'accounting/v1/pdf/generated', {
        wid: this.companyProfile.workspaceId,
        id: requestedDocumentId,
        documentType: this.documentType
      }
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((resourceUrl: string) => {
        if (requestedDocumentId === this.documentId && this.sourceMode === 'generated') {
          this.resourceUrl = resourceUrl;
          this.pdfUnavailable = !resourceUrl;
          this.isPreviewLoading = false;
          this.cdr.detectChanges();
        }
      }, (error) => {
        console.error('DocumentPdfPreviewComponent: failed to fetch generated PDF for documentId', requestedDocumentId, error);
        if (requestedDocumentId === this.documentId && this.sourceMode === 'generated') {
          this.resourceUrl = null;
          this.pdfUnavailable = true;
          this.isPreviewLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  public forceRefetch(documentId: number): void {
    if (this.sourceMode === 'generated' && !this.pdfDisabled && documentId === this.documentId) {
      this.getPdfUrl();
    }
  }

  public loadComplete(pdf?: { numPages: number }): void {
    this.pdfLoaded = true;
    if (pdf && pdf.numPages) {
      this.totalPages = pdf.numPages;
    }

    this.cdr.detectChanges();
  }

  private pendingHeightHandoff: any = null;

  private static readonly PLACEHOLDER_HEIGHT_PX = 600;

  private ancestorTransitionSettled = false;
  private measurementPendingAncestor = false;

  private get isImagePreview(): boolean {
    return this.sourceMode === 'file' && this.fileType !== 'pdf';
  }

  private clearPendingHeightHandoff(): void {
    if (this.pendingHeightHandoff) {
      clearTimeout(this.pendingHeightHandoff);
      this.pendingHeightHandoff = null;
    }
  }

  public notifyAncestorTransitionEnd(): void {
    if (this.ancestorTransitionSettled) { return; }
    this.ancestorTransitionSettled = true;
    if (this.measurementPendingAncestor) {
      this.measurementPendingAncestor = false;
      this.measureAndAnimateHeight();
    }
  }

  public measureAndAnimateHeight(): void {
    if (!this.compact) { return; }
    if (this.isImagePreview) { return; }
    if (!this.ancestorTransitionSettled) {
      this.measurementPendingAncestor = true;
      return;
    }
    this.clearPendingHeightHandoff();

    const hostEl = this.elementRef.nativeElement as HTMLElement;
    const currentHeight = (this.hostHeightPx != null) ? this.hostHeightPx : DocumentPdfPreviewComponent.PLACEHOLDER_HEIGHT_PX;

    const previousInlineHeight = hostEl.style.height;
    hostEl.style.height = 'auto';
    const targetHeight = hostEl.scrollHeight;
    hostEl.style.height = previousInlineHeight;

    const handToAuto = this.isImagePreview;

    if (Math.round(targetHeight) === Math.round(currentHeight)) {
      this.hostHeightPx = handToAuto ? null : targetHeight;
      this.cdr.detectChanges();
      return;
    }

    const requestedDocumentId = this.documentId;
    this.hostHeightPx = currentHeight;
    this.cdr.detectChanges();

    requestAnimationFrame(() => {
      if (requestedDocumentId !== this.documentId) { return; }
      this.hostHeightPx = targetHeight;
      this.cdr.detectChanges();

      if (!handToAuto) { return; }

      this.pendingHeightHandoff = setTimeout(() => {
        this.pendingHeightHandoff = null;
        if (requestedDocumentId !== this.documentId) { return; }
        this.hostHeightPx = null;
        this.cdr.detectChanges();
      }, 300); // matches the .25s CSS transition + a small buffer
    });
  }

  public prevPage(): void {
    if (this.currentPage <= 1) { return; }
    this.currentPage = this.currentPage - 1;
  }

  public nextPage(): void {
    if (this.currentPage >= this.totalPages) { return; }
    this.currentPage = this.currentPage + 1;
  }

  public onImageLoad(): void {
    if (this.compact && this.isImagePreview) {
      this.animateImageHostHeight();
      return;
    }
    this.imageLoaded = true;
    this.cdr.detectChanges();
    this.measureAndAnimateHeight();
  }

  private imageHeightAnimation: any = null;
  private imageAnimationSeq = 0;

  private animateImageHostHeight(): void {
    this.cancelImageHeightAnimation();
    const seq = this.imageAnimationSeq;
    const img = (this.elementRef.nativeElement as HTMLElement).querySelector('.document-pdf-preview-image') as any;
    const decoded: Promise<any> = (img && typeof img.decode === 'function')
      ? img.decode().catch(() => null)
      : Promise.resolve();

    this.ngZone.runOutsideAngular(() => {
      decoded.then(() => requestAnimationFrame(() => {
        if (seq !== this.imageAnimationSeq) { return; }
        this.startImageHeightAnimation();
      }));
    });
  }

  private startImageHeightAnimation(): void {
    const hostEl = this.elementRef.nativeElement as HTMLElement;
    const fromHeight = hostEl.getBoundingClientRect().height;

    this.imageLoaded = true;
    this.cdr.detectChanges();
    hostEl.classList.add('image-ready');
    const toHeight = hostEl.getBoundingClientRect().height;

    const reducedMotion = !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof (hostEl as any).animate !== 'function' || reducedMotion || Math.round(fromHeight) === Math.round(toHeight)) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const animation = (hostEl as any).animate(
        [{ height: `${fromHeight}px` }, { height: `${toHeight}px` }],
        { duration: 250, easing: 'cubic-bezier(.25, .1, .25, 1)' } // same as $animation-timing-function
      );
      animation.onfinish = () => {
        if (this.imageHeightAnimation === animation) { this.imageHeightAnimation = null; }
      };
      this.imageHeightAnimation = animation;
    });
  }

  private cancelImageHeightAnimation(): void {
    this.imageAnimationSeq++;
    if (this.imageHeightAnimation) {
      this.imageHeightAnimation.cancel();
      this.imageHeightAnimation = null;
    }
  }

  private resetImageHostHeight(): void {
    this.cancelImageHeightAnimation();
    (this.elementRef.nativeElement as HTMLElement).classList.remove('image-ready');
  }

  public textRendered(): void {}

  public pageRendered(): void {
    this.measureAndAnimateHeight();
  }

  public onPdfError(error: any): void {
    console.error('DocumentPdfPreviewComponent: pdf failed to render for documentId', this.documentId, error);
    if (!this.pdfLoaded) {
      this.pdfUnavailable = true;
      this.cdr.detectChanges();
    }
  }

  public togglePreview(): void {
    this.expanded = !this.expanded;
    this.cdr.markForCheck();
    this.scheduleFullyExpandedSync();
  }

  public setExpanded(expanded: boolean): void {
    if (this.expanded === expanded) { return; }
    this.expanded = expanded;
    this.cdr.detectChanges();
    this.scheduleFullyExpandedSync();
  }

  @HostListener('transitionend', ['$event'])
  public onHostTransitionEnd(event: TransitionEvent): void {
    if (event.target !== this.elementRef.nativeElement) { return; }
    if (event.propertyName !== 'max-width' && event.propertyName !== 'min-width') { return; }
    this.syncFullyExpanded();
  }

  private scheduleFullyExpandedSync(): void {
    this.clearFullyExpandedTimeout();
    this.fullyExpandedTimeout = setTimeout(() => this.syncFullyExpanded(), 400);
  }

  private syncFullyExpanded(): void {
    this.clearFullyExpandedTimeout();
    if (this.fullyExpanded === this.expanded) { return; }
    this.fullyExpanded = this.expanded;
    this.fullyExpandedChange.emit(this.fullyExpanded);
  }

  private clearFullyExpandedTimeout(): void {
    if (this.fullyExpandedTimeout) {
      clearTimeout(this.fullyExpandedTimeout);
      this.fullyExpandedTimeout = null;
    }
  }

  private setupResizeObserver(): void {
    if (!(window as any).ResizeObserver) { return; }

    this.resizeObserver = new (window as any).ResizeObserver((entries: any[]) => {
      this.onHostResize(entries[0].contentRect.width);
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  private onHostResize(width: number): void {
    if (this.lastRenderedWidth === null) {
      this.lastRenderedWidth = width;
      return;
    }

    this.previewScale = this.lastRenderedWidth ? width / this.lastRenderedWidth : 1;
    this.cdr.detectChanges();

    if (this.resizeSettleTimeout) {
      clearTimeout(this.resizeSettleTimeout);
    }
    this.resizeSettleTimeout = setTimeout(() => {
      this.resizeSettleTimeout = null;
      this.commitResize(width);
    }, 150);
  }

  private commitResize(width: number): void {
    if (width < 10) { return; }

    if (this.pdfViewerComponent && this.pdfLoaded && this.documentId) {
      this.pdfViewerComponent.updateSize();
    }
    this.previewScale = 1;
    this.lastRenderedWidth = width;
    this.cdr.detectChanges();
  }

  public actionClick(actionName: string): void {
    this.actionEmitted.emit(actionName);
  }

  public onOpenButtonAuxClick(event: MouseEvent): void {
    if (event.button !== 1 || !this.openDocumentUrl) { return; }
    event.preventDefault();
    const url = this.router.serializeUrl(this.router.createUrlTree([this.openDocumentUrl]));
    window.open(url, '_blank');
  }

  public footerActionClick(actionName: string): void {
    if (this.loadingAction) { return; }
    if (this.clearLoadingAction$ && !FOOTER_ACTIONS_WITHOUT_SPINNER.includes(actionName)) {
      this.setLoadingAction(actionName);
    }
    this.actionEmitted.emit(actionName);
  }

  public trackByIndex(index: number): number {
    return index;
  }

  private subscribeToClearLoading(): void {
    if (this.clearLoadingSubscription) {
      this.clearLoadingSubscription.unsubscribe();
      this.clearLoadingSubscription = null;
    }
    if (!this.clearLoadingAction$) { return; }

    this.clearLoadingSubscription = this.clearLoadingAction$
      .pipe(takeUntil(this.destroy$))
      .subscribe((actionName: string) => {
        if (this.loadingAction && actionName === this.loadingAction) {
          this.setLoadingAction(null);
        }
      });
  }

  private setLoadingAction(actionName: string): void {
    this.loadingAction = actionName;
    this.updateRenderedFooterActions();
    this.cdr.markForCheck();
  }

  private updateRenderedFooterActions(): void {
    this.renderedFooterActions = (this.footerActions || [])
      .map((btn: NavBarBtnModel) => this.isFooterButtonLoading(btn) ? this.withFooterSpinner(btn) : btn);
  }

  private isFooterButtonLoading(btn: NavBarBtnModel): boolean {
    if (!this.loadingAction) { return false; }
    return btn.actionName === this.loadingAction
      || (btn.actions || []).some(action => action.actionName === this.loadingAction);
  }

  private withFooterSpinner(btn: NavBarBtnModel): NavBarBtnModel {
    const spinnerColorFor = (classes: string, spinnerColor: string) =>
      spinnerColor || ((classes || '').includes('btn-white') ? FOOTER_SPINNER_COLOR : undefined);

    return {
      ...btn,
      isLoading: true,
      spinnerColor: spinnerColorFor(btn.classes, btn.spinnerColor),
      actions: btn.isMoreAction && btn.actions
        ? btn.actions.map(action => ({ ...action, spinnerColor: spinnerColorFor(action.classes || btn.classes, action.spinnerColor) }))
        : btn.actions,
    };
  }

  public get isZoomed(): boolean {
    return this.zoomLevel > this.zoomMin;
  }

  public get zoomPercent(): number {
    return Math.round(this.zoomLevel * 100);
  }

  public get showZoomControls(): boolean {
    return this.enableZoom && !!this.documentId && !this.pdfUnavailable && !this.showLoader;
  }

  public get showPageNav(): boolean {
    return this.compact && this.totalPages > 0;
  }

  public get isZoomInDisabled(): boolean {
    return this.zoomLevel >= this.zoomMax - 0.001;
  }

  public get isZoomOutDisabled(): boolean {
    return this.zoomLevel <= this.zoomMin + 0.001;
  }

  public zoomIn(): void {
    if (this.isZoomInDisabled) { return; }
    this.zoomLevel = Math.round(Math.min(this.zoomMax, this.zoomLevel + this.zoomStep) * 100) / 100;
    this.cdr.detectChanges();
    this.refreshPdfScrollbar();
  }

  public zoomOut(): void {
    if (this.isZoomOutDisabled) { return; }
    this.zoomLevel = Math.round(Math.max(this.zoomMin, this.zoomLevel - this.zoomStep) * 100) / 100;
    this.cdr.detectChanges();
    this.refreshPdfScrollbar();
  }

  // The <perfect-scrollbar> host tag never scrolls itself (overflow:hidden) — the actual scrollable
  // node is the inner .ps div the library's own directive attaches to internally.
  private getPdfScrollElement(): HTMLElement | null {
    return (this.pdfScrollContainerRef && this.pdfScrollContainerRef.directiveRef)
      ? this.pdfScrollContainerRef.directiveRef.elementRef.nativeElement
      : null;
  }

  private refreshPdfScrollbar(): void {
    if (!this.pdfScrollContainerRef || !this.pdfScrollContainerRef.directiveRef) { return; }
    this.pdfScrollContainerRef.directiveRef.update();
  }

  // Bound once so add/removeEventListener target the same function reference.
  private readonly boundPanMove = (event: MouseEvent) => this.queuePanMove(event);
  private readonly boundPanEnd = () => this.stopPan();
  private pendingPanEvent: MouseEvent | null = null;
  private panRafId: number | null = null;

  public onPdfPanStart(event: MouseEvent): void {
    const container = this.getPdfScrollElement();
    if (!this.enableZoom || !this.isZoomed || !container) { return; }

    this.isPanning = true;
    this.panStartX = event.clientX;
    this.panStartY = event.clientY;
    this.panScrollLeft = container.scrollLeft;
    this.panScrollTop = container.scrollTop;
    event.preventDefault();

    // Perf: `document:mousemove` fires very frequently (can be 100s/sec). A plain @HostListener
    // runs inside NgZone, so every single move was triggering a change-detection pass for the
    // WHOLE app
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.boundPanMove);
      document.addEventListener('mouseup', this.boundPanEnd);
    });

    this.cdr.detectChanges(); // reflect isPanning -> [class.document-pdf-preview-panning] (grab cursor)
  }

  private queuePanMove(event: MouseEvent): void {
    this.pendingPanEvent = event;
    if (this.panRafId !== null) { return; }

    this.panRafId = requestAnimationFrame(() => {
      this.panRafId = null;
      const container = this.getPdfScrollElement();
      if (!container || !this.pendingPanEvent) { return; }

      container.scrollLeft = this.panScrollLeft - (this.pendingPanEvent.clientX - this.panStartX);
      container.scrollTop = this.panScrollTop - (this.pendingPanEvent.clientY - this.panStartY);
    });
  }

  private stopPan(): void {
    document.removeEventListener('mousemove', this.boundPanMove);
    document.removeEventListener('mouseup', this.boundPanEnd);
    if (this.panRafId !== null) {
      cancelAnimationFrame(this.panRafId);
      this.panRafId = null;
    }

    this.ngZone.run(() => {
      this.isPanning = false;
      this.cdr.detectChanges();
    });
  }

  // Resets zoom/pan whenever the previewed content itself changes (new document selected, or a
  // new file swapped in).
  private resetZoom(): void {
    if (this.isPanning) {
      this.stopPan();
    }
    this.zoomLevel = this.zoomMin;
    this.refreshPdfScrollbar();
  }

  ngOnDestroy() {
    this.clearFullyExpandedTimeout();
    this.clearPendingHeightHandoff();
    this.cancelImageHeightAnimation();

    // In case the component is destroyed mid-drag
    document.removeEventListener('mousemove', this.boundPanMove);
    document.removeEventListener('mouseup', this.boundPanEnd);
    if (this.panRafId !== null) {
      cancelAnimationFrame(this.panRafId);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.resizeSettleTimeout) {
      clearTimeout(this.resizeSettleTimeout);
    }
    this.destroy$.next(true);
    this.destroy$.complete();
  }

}
